import { useEffect, useState } from 'react';
import type { ApiClient, Systemversion } from '../api';

/**
 * F312 (Eigentümer 02.11.2026): Die live laufende Version steht in der Kopfzeile rechts neben „Suchen und springen“,
 * kurz als `vX.XX.X` - aus „v0.53.3-mehrhaus-viewer“ wird „v0.53.3“, der Namenszusatz des Releases entfällt.
 *
 * QUELLE ist dieselbe angemeldete Route wie die Fußzeile der Startseite (FV1): `GET /api/system/version`. Deren
 * Releasename steht als Assembly-Metadatum im laufenden API-Abbild, gehört also fest zu dem Code, der gerade läuft -
 * kein Wert aus dem Web-Bündel. Ohne gesetzten Bau („Entwicklungsstand“), bei einem Namen ohne Versionsnummer oder
 * wenn die Abfrage scheitert, zeigt die Kopfzeile NICHTS (kein Platzhalter, keine Fehlermeldung).
 */

/** Versionsnummer am Anfang des Releasenamens: optional „v“, dann drei Zahlen, danach keine weitere Ziffer/kein Punkt. */
const VERSION = /^v?(\d+)\.(\d+)\.(\d+)(?![\d.])/i;

/** „v0.53.3-mehrhaus-viewer“ → „v0.53.3“; „0.53.4“ → „v0.53.4“; alles ohne Versionsnummer → null. */
export function kurzversion(release: string | null | undefined): string | null {
  const treffer = VERSION.exec((release ?? '').trim());
  return treffer ? `v${treffer[1]}.${treffer[2]}.${treffer[3]}` : null;
}

/** Was die Kopfzeile zeigt: nur bei gesetztem Bau die gekürzte Version, sonst null. */
export function kopfversion(version: Systemversion | null | undefined): string | null {
  if (!version || !version.gesetzt) return null;
  return kurzversion(version.release);
}

/**
 * Holt die Version einmal nach der Anmeldung - sie ändert sich nur mit einem neuen Release. Vor der Anmeldung wird
 * nicht gefragt (die Route verlangt sie ohnehin); scheitert die Abfrage, bleibt es bei null.
 */
export function useKopfversion(api: Pick<ApiClient, 'systemversion'>, angemeldet: boolean): string | null {
  const [version, setVersion] = useState<string | null>(null);
  useEffect(() => {
    if (!angemeldet) return undefined;
    let aktiv = true;
    api.systemversion()
      .then((v) => { if (aktiv) setVersion(kopfversion(v)); })
      .catch(() => { if (aktiv) setVersion(null); });
    return () => { aktiv = false; };
  }, [api, angemeldet]);
  return angemeldet ? version : null;
}
